import { SPATIAL_OPERATIONS_SUBLINE } from '../spatialOperations/spatialOperationsPresentation';
import { CAMERA_MEASUREMENT_PRIVACY } from '../cameraMeasurement/cameraMeasurementPresentation';
import { zoneOverlayPolygon } from '../liveView/zoneOverlayGeometry';
import { SHOWCASE_LIVE_PHASE_LABEL, type ShowcaseLivePhase } from './liveNowAdapter';
import type { Zone } from '../../services/zonesApi';

export const SHOWCASE_PATH = '/showcase';
export const SHOWCASE_BRAND = 'Edgentik';
export const SHOWCASE_BRAND_LINE = 'Physical AI for Industrial Safety';
export const SHOWCASE_DEFAULT_EVENT_LABEL = 'Edgentik Live Demo';
/** Neutral demonstration label. An event name appears only from an explicit `event` query. */
export const SHOWCASE_EVENT_LABEL = SHOWCASE_DEFAULT_EVENT_LABEL;
export const SHOWCASE_TITLE = 'Edgentik Live Demo';

export function showcaseEventLabel(search?: string | null): string {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const configured = String(params.get('event') || params.get('event_name') || '').trim();
  return configured || SHOWCASE_DEFAULT_EVENT_LABEL;
}
export const SHOWCASE_STORY = 'NOW → REPEATING → VERIFY';
export const SHOWCASE_LIVE_CONDITION = 'RESTRICTED AREA EXPOSURE';
export const SHOWCASE_FORKLIFT_CONDITION = 'FORKLIFT–PEDESTRIAN PROXIMITY';
export const SHOWCASE_DEFAULT_AREA = 'LOADING DOORWAY';
export const SHOWCASE_EVIDENCE_LABEL = 'EVIDENCE';
export const SHOWCASE_LIVE_CAMERA_LABEL = 'LIVE CAMERA';
export const SHOWCASE_STALE_CAMERA_LABEL = 'STALE CAMERA';
/** Live camera ~49% and measurements ~51%. The booth is not a 72/28 split. */
export const SHOWCASE_LIVE_BOOTH_COLUMNS = 'minmax(0, 0.49fr) minmax(0, 0.51fr)';
export const SHOWCASE_LIVE_BOOTH_CAMERA_RATIO = '0.49';
export const SHOWCASE_LIVE_BOOTH_TYPE = {
  condition: { xs: 24, lg: 28 },
  state: { xs: 48, lg: 64 },
  explanation: { xs: 20, lg: 24 },
  supporting: { xs: 16, lg: 18 },
} as const;
export const SHOWCASE_LIVE_EVIDENCE_UNAVAILABLE = 'LIVE EVIDENCE UNAVAILABLE';
export const SHOWCASE_CAMERA_STREAM_UNAVAILABLE = 'Camera stream unavailable';
export const SHOWCASE_SOURCE_LIVE_BOOTH = 'Live Booth';
export const SHOWCASE_SOURCE_FORKLIFT = 'Room B';
export const SHOWCASE_SOURCE_ROOM_B = SHOWCASE_SOURCE_FORKLIFT;
export const SHOWCASE_FORKLIFT_SCENARIO = 'Recorded industrial scenario.';
export const SHOWCASE_FORKLIFT_NOT_LIVE = 'Not a live condition measurement.';
export const SHOWCASE_CLEAR_SENTENCE = 'No person currently in the monitored area.';
export const SHOWCASE_OBSERVING_SENTENCE = 'Person in monitored area.';
export const SHOWCASE_OBSERVING_SUPPORT = 'Waiting for the configured condition threshold.';
export const SHOWCASE_ACTIVE_SENTENCE = 'Restricted Area Exposure detected.';
export const SHOWCASE_UNAVAILABLE_SENTENCE = 'STATE UNAVAILABLE. Live condition state could not be read.';
export const SHOWCASE_POLICY_FACT = 'Becomes a condition after the configured presence threshold.';
export const SHOWCASE_VERIFY_NAV = 'Did it change after action?';
export const SHOWCASE_NOW_HEADLINE = 'What is happening?';
export const SHOWCASE_REPEATING_HEADLINE = 'What keeps happening?';
export const SHOWCASE_VERIFY_HEADLINE = 'Did the condition change?';
export const SHOWCASE_SUBLINE = SPATIAL_OPERATIONS_SUBLINE;
export const SHOWCASE_PRIVACY = CAMERA_MEASUREMENT_PRIVACY;
export const SHOWCASE_NOT_CUSTOMER_RESULT = 'Demonstration data. Not a customer-site result.';

export const SHOWCASE_LABEL_LIVE = 'LIVE';
export const SHOWCASE_LABEL_STALE = 'STALE';
export const SHOWCASE_LABEL_RECORDED = 'RECORDED';
export const SHOWCASE_LABEL_RECORDED_FOOTAGE = 'Recorded footage';
export const SHOWCASE_LABEL_PROCESSED_LIVE = 'PROCESSED LIVE';
export const SHOWCASE_LABEL_DEMO_DATASET = 'DEMO DATASET';
export const SHOWCASE_LABEL_FALLBACK = 'FALLBACK';
export const SHOWCASE_LABEL_STATIC = 'STATIC';

export const SHOWCASE_CTA_QUESTION = 'What condition would you want to measure at your site?';
export const SHOWCASE_CTA_WALKTHROUGH = 'Book a site walkthrough';
export const SHOWCASE_CTA_ASK_OPERATOR = 'Ask the operator';
export const SHOWCASE_WALKTHROUGH_URL = 'https://edgentik.com/spatial-operations-view.html';
export const SHOWCASE_WALKTHROUGH_QR = '/showcase-assets/cta-qr.png';
export const SHOWCASE_CTA_QR_PX = 88;
export const SHOWCASE_SESSION_KEY = 'edgentik.showcase.presentation.v1';
export const SHOWCASE_LABEL_RECORDED_PROCESSED_LIVE = 'RECORDED · PROCESSED LIVE';
/** This session did not prove product MJPEG/runtime processing on the Room B source. */
export const SHOWCASE_FORKLIFT_PROCESSING_PROVEN = false;
/** Room B Restricted-area entry. Not proximity. Not the booth RAE zone. */
export const SHOWCASE_ROOM_B_RAE_CONDITION_ID = 'd8d39195-eba6-5b11-92f2-ad6671071df9';
/** Room B Forklift-pedestrian proximity. Entire-camera-view scope. */
export const SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID = '6d79065c-c8fa-5193-a7eb-6a922c6cea64';
/** Room B recorded walkway camera. Same source as Spatial Ops / live-view. */
export const SHOWCASE_ROOM_B_CAMERA_ID = 'cam_walkway_zone_demo';
/** Warehouse device for Showcase cameras when the URL omits booth_device_id. */
export const SHOWCASE_BOOTH_DEVICE_ID = 'demo5';
export const SHOWCASE_ROOM_B_DEVICE_ID = SHOWCASE_BOOTH_DEVICE_ID;
/** @deprecated Misnamed alias of SHOWCASE_ROOM_B_RAE_CONDITION_ID. Do not treat as proximity. */
export const SHOWCASE_FORKLIFT_SPATIAL_CONDITION_ID = SHOWCASE_ROOM_B_RAE_CONDITION_ID;

export function showcaseStoryMode(pane: ShowcasePane): 'now' | 'repeating' | 'verify' | 'ppe' {
  if (pane === 'repeating') return 'repeating';
  if (pane === 'verify') return 'verify';
  if (pane === 'ppe') return 'ppe';
  return 'now';
}

export function showcaseSectionHeadline(pane: ShowcasePane): string {
  const mode = showcaseStoryMode(pane);
  if (mode === 'repeating') return SHOWCASE_REPEATING_HEADLINE;
  if (mode === 'verify') return SHOWCASE_VERIFY_HEADLINE;
  if (mode === 'ppe') return 'Visible PPE';
  return SHOWCASE_NOW_HEADLINE;
}

export function forkliftVisitorLabel(processingProven = SHOWCASE_FORKLIFT_PROCESSING_PROVEN): string {
  return processingProven ? SHOWCASE_LABEL_RECORDED_PROCESSED_LIVE : SHOWCASE_LABEL_RECORDED;
}

export function showcaseStateLabel(phase: ShowcaseLivePhase): string {
  return SHOWCASE_LIVE_PHASE_LABEL[phase] || SHOWCASE_LIVE_PHASE_LABEL.unknown;
}

/** Actual dwell only. Null or non-positive stays unset so the visitor copy does not invent a duration. */
export function showcaseDwellClause(dwellSeconds: number | null | undefined): string {
  if (dwellSeconds == null || !Number.isFinite(dwellSeconds) || dwellSeconds <= 0) return '';
  const rounded = Math.round(dwellSeconds * 10) / 10;
  const shown = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return ` for ${shown} sec`;
}

/** Visitor sentences only. Does not derive phase or join incidents. */
export function showcaseVisitorSentence(phase: ShowcaseLivePhase, dwellSeconds?: number | null): string {
  const dwell = showcaseDwellClause(dwellSeconds);
  if (phase === 'clear') return SHOWCASE_CLEAR_SENTENCE;
  if (phase === 'observing') return dwell ? `Person in monitored area${dwell}.` : SHOWCASE_OBSERVING_SENTENCE;
  if (phase === 'active') {
    return dwell
      ? `${SHOWCASE_ACTIVE_SENTENCE} Condition active${dwell}.`
      : SHOWCASE_ACTIVE_SENTENCE;
  }
  return SHOWCASE_UNAVAILABLE_SENTENCE;
}

export function showcaseAreaContext(areaName?: string | null): string {
  const name = String(areaName || '').trim();
  if (!name || (/[_-]/.test(name) && !/\s/.test(name))) return SHOWCASE_DEFAULT_AREA;
  return name;
}

export function showcaseLiveBoothAreaName(areaName?: string | null): string | null {
  const name = String(areaName || '').trim();
  if (!name) return null;
  if (/[_-]/.test(name) && !/\s/.test(name)) return null;
  return name;
}

export const SHOWCASE_LIVE_TIMING_LINES = [
  {
    id: 'observing',
    label: 'OBSERVING',
    text: 'Person in the marked area.',
  },
  {
    id: 'active',
    label: 'ACTIVE',
    text: 'Opens after 2 seconds.',
  },
  {
    id: 'clear',
    label: 'CLEAR',
    text: 'Closes after empty for 10 seconds.',
  },
] as const;

export function showcaseTriggerAfterLine(thresholdSeconds?: number | null): string | null {
  if (thresholdSeconds == null || !Number.isFinite(thresholdSeconds) || thresholdSeconds <= 0) return null;
  const rounded = Math.round(thresholdSeconds * 10) / 10;
  const shown = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  const unit = rounded === 1 ? 'second' : 'seconds';
  return `Triggers after ${shown} ${unit} in the marked area.`;
}

export const SHOWCASE_PANES = ['booth', 'forklift', 'repeating', 'verify', 'ppe'] as const;
export type ShowcasePane = (typeof SHOWCASE_PANES)[number];
export const SHOWCASE_SOURCES = ['booth', 'room_b'] as const;
export type ShowcaseSource = (typeof SHOWCASE_SOURCES)[number];
export const SHOWCASE_ROOM_B_CONDITIONS = ['rae', 'proximity'] as const;
export type ShowcaseRoomBCondition = (typeof SHOWCASE_ROOM_B_CONDITIONS)[number];

export function showcaseNowPane(source: ShowcaseSource): ShowcasePane {
  return source === 'room_b' ? 'forklift' : 'booth';
}

/** Keys 3/4 and leftover pane=3/4 URLs render the current source dashboard, not a separate visitor page. */
export function showcaseVisitorPane(pane: ShowcasePane, source: ShowcaseSource): ShowcasePane {
  if (pane === 'ppe') return 'ppe';
  if (pane === 'repeating' || pane === 'verify') return showcaseNowPane(source);
  if (pane === 'forklift' || source === 'room_b') return 'forklift';
  return 'booth';
}

export function showcaseSourceFromPane(pane: ShowcasePane, source?: ShowcaseSource | null): ShowcaseSource {
  if (pane === 'forklift') return 'room_b';
  if (pane === 'booth') return 'booth';
  return source === 'room_b' ? 'room_b' : 'booth';
}

export function parseShowcaseSource(value: unknown): ShowcaseSource | null {
  const raw = text(value).toLowerCase().replace(/-/g, '_');
  if (raw === 'booth' || raw === 'live' || raw === 'live_booth') return 'booth';
  if (raw === 'room_b' || raw === 'roomb' || raw === 'forklift' || raw === 'recorded') return 'room_b';
  return null;
}

export function parseShowcaseRoomBCondition(value: unknown): ShowcaseRoomBCondition | null {
  const raw = text(value).toLowerCase().replace(/-/g, '_');
  if (raw === 'rae' || raw === 'restricted' || raw === 'restricted_area_entry') return 'rae';
  if (raw === 'proximity' || raw === 'prox' || raw === 'near_miss' || raw === 'forklift') return 'proximity';
  return null;
}

export const SHOWCASE_PANE_KEYS: Record<string, ShowcasePane> = {
  '1': 'booth',
  '2': 'forklift',
  '3': 'repeating',
  '4': 'verify',
  '5': 'ppe',
};

export const SHOWCASE_PANE_TO_KEY: Record<ShowcasePane, string> = {
  booth: '1',
  forklift: '2',
  repeating: '3',
  verify: '4',
  ppe: '5',
};

export type ShowcaseSearch = {
  pane: ShowcasePane;
  fallback: boolean;
  source: ShowcaseSource;
  roomBCondition: ShowcaseRoomBCondition;
  boothCameraId: string | null;
  forkliftCameraId: string | null;
  boothZoneId: string | null;
  boothPolicyId: string | null;
  boothDeviceId: string | null;
  forkliftDeviceId: string | null;
  forkliftZoneId: string | null;
  forkliftConditionId: string | null;
  roomBProximityConditionId: string | null;
  ctaUrl: string | null;
  qrSrc: string | null;
  ppeEnabled: boolean;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function boolParam(value: string | null): boolean {
  const next = text(value).toLowerCase();
  return next === '1' || next === 'true' || next === 'yes';
}

export function parseShowcasePane(value: unknown): ShowcasePane | null {
  const raw = text(value).toLowerCase();
  if (raw === '1' || raw === 'booth' || raw === 'live') return 'booth';
  if (raw === '2' || raw === 'forklift' || raw === 'recorded') return 'forklift';
  if (raw === '3' || raw === 'repeating') return 'repeating';
  if (raw === '4' || raw === 'verify') return 'verify';
  if (raw === '5' || raw === 'ppe') return 'ppe';
  return null;
}

export function defaultShowcaseSearch(): ShowcaseSearch {
  return {
    pane: 'booth',
    fallback: false,
    source: 'booth',
    roomBCondition: 'rae',
    boothCameraId: null,
    forkliftCameraId: null,
    boothZoneId: null,
    boothPolicyId: null,
    boothDeviceId: null,
    forkliftDeviceId: null,
    forkliftZoneId: null,
    forkliftConditionId: null,
    roomBProximityConditionId: null,
    ctaUrl: null,
    qrSrc: null,
    ppeEnabled: false,
  };
}

export function envShowcaseDefaults(): Pick<
  ShowcaseSearch,
  'ctaUrl' | 'qrSrc' | 'ppeEnabled' | 'boothCameraId' | 'forkliftCameraId' | 'boothZoneId' | 'boothPolicyId' | 'boothDeviceId' | 'forkliftDeviceId' | 'forkliftZoneId' | 'forkliftConditionId' | 'roomBProximityConditionId'
> {
  return {
    ctaUrl: text(process.env.REACT_APP_SHOWCASE_CTA_URL) || SHOWCASE_WALKTHROUGH_URL,
    qrSrc: text(process.env.REACT_APP_SHOWCASE_QR_SRC) || SHOWCASE_WALKTHROUGH_QR,
    ppeEnabled: text(process.env.REACT_APP_SHOWCASE_PPE).toLowerCase() === 'true',
    boothCameraId: text(process.env.REACT_APP_SHOWCASE_BOOTH_CAMERA_ID) || null,
    forkliftCameraId: text(process.env.REACT_APP_SHOWCASE_FORKLIFT_CAMERA_ID) || SHOWCASE_ROOM_B_CAMERA_ID,
    boothZoneId: text(process.env.REACT_APP_SHOWCASE_BOOTH_ZONE_ID) || null,
    boothPolicyId: text(process.env.REACT_APP_SHOWCASE_BOOTH_POLICY_ID) || null,
    boothDeviceId: text(process.env.REACT_APP_SHOWCASE_BOOTH_DEVICE_ID) || SHOWCASE_BOOTH_DEVICE_ID,
    forkliftDeviceId: text(process.env.REACT_APP_SHOWCASE_FORKLIFT_DEVICE_ID) || SHOWCASE_ROOM_B_DEVICE_ID,
    forkliftZoneId: text(process.env.REACT_APP_SHOWCASE_FORKLIFT_ZONE_ID) || null,
    forkliftConditionId: text(process.env.REACT_APP_SHOWCASE_FORKLIFT_CONDITION_ID) || SHOWCASE_ROOM_B_RAE_CONDITION_ID,
    roomBProximityConditionId: text(process.env.REACT_APP_SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID) || SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID,
  };
}

export function parseShowcaseSearch(search: string, hash = ''): ShowcaseSearch {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const env = envShowcaseDefaults();
  const urlPane = parseShowcasePane(params.get('pane') || hashParams.get('pane'));
  const source = parseShowcaseSource(params.get('source') || hashParams.get('source'))
    || showcaseSourceFromPane(urlPane || 'booth', null);
  const pane = urlPane || showcaseNowPane(source);
  const roomBCondition = parseShowcaseRoomBCondition(
    params.get('room_b_condition') || params.get('condition') || hashParams.get('room_b_condition'),
  ) || 'rae';
  const boothDeviceId = text(params.get('booth_device_id')) || env.boothDeviceId;
  return {
    pane,
    fallback: boolParam(params.get('fallback') || hashParams.get('fallback')),
    source,
    roomBCondition,
    boothCameraId: text(params.get('booth_camera_id')) || text(params.get('booth_id')) || env.boothCameraId,
    forkliftCameraId: text(params.get('forklift_camera_id')) || env.forkliftCameraId,
    boothZoneId: text(params.get('booth_zone_id')) || env.boothZoneId,
    boothPolicyId: text(params.get('booth_policy_id')) || env.boothPolicyId,
    boothDeviceId,
    forkliftDeviceId: text(params.get('forklift_device_id')) || env.forkliftDeviceId || boothDeviceId,
    forkliftZoneId: text(params.get('forklift_zone_id')) || env.forkliftZoneId,
    forkliftConditionId: text(params.get('forklift_condition_id')) || env.forkliftConditionId,
    roomBProximityConditionId: text(params.get('room_b_proximity_condition_id')) || env.roomBProximityConditionId,
    ctaUrl: text(params.get('cta_url')) || env.ctaUrl,
    qrSrc: text(params.get('qr_src')) || env.qrSrc,
    ppeEnabled: boolParam(params.get('ppe')) || env.ppeEnabled,
  };
}

export function buildShowcasePath(args: {
  pane?: ShowcasePane;
  fallback?: boolean;
  source?: ShowcaseSource;
  roomBCondition?: ShowcaseRoomBCondition;
  currentSearch?: string;
  currentHash?: string;
}): string {
  const current = parseShowcaseSearch(args.currentSearch || '', args.currentHash || '');
  const pane = args.pane ?? current.pane;
  const fallback = args.fallback ?? current.fallback;
  const source = args.source ?? showcaseSourceFromPane(pane, current.source);
  const roomBCondition = args.roomBCondition ?? current.roomBCondition;
  const next = new URLSearchParams();
  if (current.boothCameraId) next.set('booth_camera_id', current.boothCameraId);
  if (current.forkliftCameraId) next.set('forklift_camera_id', current.forkliftCameraId);
  if (current.boothZoneId) next.set('booth_zone_id', current.boothZoneId);
  if (current.boothPolicyId) next.set('booth_policy_id', current.boothPolicyId);
  if (current.boothDeviceId) next.set('booth_device_id', current.boothDeviceId);
  if (current.forkliftDeviceId) next.set('forklift_device_id', current.forkliftDeviceId);
  if (current.forkliftZoneId) next.set('forklift_zone_id', current.forkliftZoneId);
  if (current.forkliftConditionId) next.set('forklift_condition_id', current.forkliftConditionId);
  if (current.roomBProximityConditionId && current.roomBProximityConditionId !== SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID) {
    next.set('room_b_proximity_condition_id', current.roomBProximityConditionId);
  }
  if (current.ctaUrl) next.set('cta_url', current.ctaUrl);
  if (current.qrSrc) next.set('qr_src', current.qrSrc);
  if (current.ppeEnabled) next.set('ppe', '1');
  if (pane !== 'booth') next.set('pane', SHOWCASE_PANE_TO_KEY[pane]);
  if (source !== 'booth') next.set('source', source);
  if (roomBCondition !== 'rae') next.set('room_b_condition', roomBCondition);
  if (fallback) next.set('fallback', '1');
  const query = next.toString();
  return query ? `${SHOWCASE_PATH}?${query}` : SHOWCASE_PATH;
}

export type ShowcaseSessionState = {
  pane: ShowcasePane;
  fallback: boolean;
  source: ShowcaseSource;
  roomBCondition: ShowcaseRoomBCondition;
};

export function readShowcaseSession(): ShowcaseSessionState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SHOWCASE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ShowcaseSessionState>;
    const pane = parseShowcasePane(parsed.pane);
    if (!pane) return null;
    const source = parseShowcaseSource(parsed.source) || showcaseSourceFromPane(pane, null);
    const roomBCondition = parseShowcaseRoomBCondition(parsed.roomBCondition) || 'rae';
    return { pane, fallback: Boolean(parsed.fallback), source, roomBCondition };
  } catch {
    return null;
  }
}

export function writeShowcaseSession(state: ShowcaseSessionState): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SHOWCASE_SESSION_KEY, JSON.stringify({
      pane: state.pane,
      fallback: Boolean(state.fallback),
      source: state.source,
      roomBCondition: state.roomBCondition,
    }));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearShowcaseSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SHOWCASE_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/** Remembers which customer booth route last owned the presentation session. */
export const SHOWCASE_CUSTOMER_ROUTE_KEY = 'edgentik.showcase.customerRoute.v1';
export const SHOWCASE2_PATH = '/showcase2';

export function isCustomerShowcasePath(pathname: string): boolean {
  return pathname === SHOWCASE_PATH || pathname === SHOWCASE2_PATH;
}

export function readShowcaseCustomerRoute(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.sessionStorage.getItem(SHOWCASE_CUSTOMER_ROUTE_KEY);
    return value === SHOWCASE_PATH || value === SHOWCASE2_PATH ? value : null;
  } catch {
    return null;
  }
}

export function writeShowcaseCustomerRoute(pathname: string): void {
  if (typeof window === 'undefined' || !isCustomerShowcasePath(pathname)) return;
  try {
    window.sessionStorage.setItem(SHOWCASE_CUSTOMER_ROUTE_KEY, pathname);
  } catch {
    /* ignore quota / private mode */
  }
}

export function showcaseCustomerRouteChanged(pathname: string): boolean {
  if (!isCustomerShowcasePath(pathname)) return false;
  const previous = readShowcaseCustomerRoute();
  return Boolean(previous && previous !== pathname);
}

/** Operator URLs may still name the recorded Room B source. Customer device links do not. */
export function showcaseUrlSelectsRecordedSource(rawSearch = '', rawHash = ''): boolean {
  const params = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
  const hashParams = new URLSearchParams(rawHash.startsWith('#') ? rawHash.slice(1) : rawHash);
  const source = parseShowcaseSource(params.get('source') || hashParams.get('source'));
  const pane = parseShowcasePane(params.get('pane') || hashParams.get('pane'));
  return source === 'room_b' || pane === 'forklift';
}

export function customerLiveBoothSearch(search: ShowcaseSearch): ShowcaseSearch {
  return {
    ...search,
    pane: 'booth',
    fallback: false,
    source: 'booth',
    roomBCondition: 'rae',
  };
}

/**
 * Customer booth routes stay on the live booth.
 * A stored Room B source must not follow a switch between /showcase and /showcase2,
 * and it must not revive on a customer URL that did not ask for the recorded source.
 */
export function shouldForceCustomerLiveBooth(pathname: string, rawSearch = '', rawHash = ''): boolean {
  if (!isCustomerShowcasePath(pathname)) return false;
  if (showcaseCustomerRouteChanged(pathname)) return true;
  const stored = readShowcaseSession();
  return !showcaseUrlSelectsRecordedSource(rawSearch, rawHash)
    && (stored?.source === 'room_b' || stored?.pane === 'forklift');
}

export function stripRecordedShowcaseParams(search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  ['source', 'pane', 'room_b_condition', 'condition', 'fallback'].forEach((key) => params.delete(key));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function resolveShowcasePaneOnLoad(
  search: ShowcaseSearch,
  rawSearch = '',
  rawHash = '',
  pathname = '',
): ShowcaseSearch {
  if (shouldForceCustomerLiveBooth(pathname, rawSearch, rawHash)) {
    return customerLiveBoothSearch(search);
  }
  const params = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
  const hashParams = new URLSearchParams(rawHash.startsWith('#') ? rawHash.slice(1) : rawHash);
  const stored = readShowcaseSession();
  const urlHasPane = params.has('pane') || hashParams.has('pane') || params.has('fallback') || hashParams.has('fallback');
  const urlHasSource = params.has('source') || hashParams.has('source') || params.has('room_b_condition') || params.has('condition');
  if (urlHasPane) {
    return {
      ...search,
      source: urlHasSource ? search.source : (stored?.source || search.source),
      roomBCondition: urlHasSource ? search.roomBCondition : (stored?.roomBCondition || search.roomBCondition),
    };
  }
  if (!stored) return search;
  return {
    ...search,
    pane: stored.pane,
    fallback: stored.fallback,
    source: stored.source,
    roomBCondition: stored.roomBCondition,
  };
}

export function applyShowcaseKey(args: {
  key: string;
  pane: ShowcasePane;
  fallback: boolean;
  ppeEnabled: boolean;
  source?: ShowcaseSource;
  roomBCondition?: ShowcaseRoomBCondition;
}): {
  pane: ShowcasePane;
  fallback: boolean;
  source: ShowcaseSource;
  roomBCondition: ShowcaseRoomBCondition;
  lampTest: boolean;
  handled: boolean;
} {
  const source = args.source || showcaseSourceFromPane(args.pane, null);
  const roomBCondition = args.roomBCondition || 'rae';
  const key = args.key.length === 1 ? args.key.toLowerCase() : args.key;
  if (key === '1') {
    return { pane: 'booth', fallback: args.fallback, source: 'booth', roomBCondition, lampTest: false, handled: true };
  }
  if (key === '2') {
    return { pane: 'forklift', fallback: args.fallback, source: 'room_b', roomBCondition, lampTest: false, handled: true };
  }
  if (key === '3' || key === '4') {
    return {
      pane: showcaseNowPane(source),
      fallback: args.fallback,
      source,
      roomBCondition,
      lampTest: false,
      handled: true,
    };
  }
  if (key === '5') {
    if (!args.ppeEnabled) {
      return { pane: args.pane, fallback: args.fallback, source, roomBCondition, lampTest: false, handled: true };
    }
    return { pane: 'ppe', fallback: args.fallback, source, roomBCondition, lampTest: false, handled: true };
  }
  if (key === 'f') {
    return { pane: args.pane, fallback: true, source, roomBCondition, lampTest: false, handled: true };
  }
  if (key === 'l') {
    return { pane: args.pane, fallback: args.fallback, source, roomBCondition, lampTest: true, handled: true };
  }
  if (key === 'r' || key === '0' || args.key === 'Escape' || args.key === 'escape') {
    return { pane: 'booth', fallback: false, source: 'booth', roomBCondition: 'rae', lampTest: false, handled: true };
  }
  return { pane: args.pane, fallback: args.fallback, source, roomBCondition, lampTest: false, handled: false };
}

export function nowStreamsActive(_pane: ShowcasePane, fallback: boolean): boolean {
  return !fallback;
}

export function presentationResetState(): ShowcaseSessionState {
  return { pane: 'booth', fallback: false, source: 'booth', roomBCondition: 'rae' };
}

export type ShowcaseLastKnownGoodPolygon = {
  cameraId: string;
  zoneId: string;
  polygon: Array<[number, number]>;
};

function isRealZonePolygon(polygon: unknown): polygon is Array<[number, number]> {
  if (!Array.isArray(polygon) || polygon.length < 3) return false;
  return polygon.every((pair) => (
    Array.isArray(pair)
    && Number.isFinite(Number(pair[0]))
    && Number.isFinite(Number(pair[1]))
  ));
}

function copyRealZonePolygon(polygon: Array<[number, number]> | number[][]): Array<[number, number]> {
  return polygon.map((pair) => [Number(pair[0]), Number(pair[1])] as [number, number]);
}

/** Last successfully read real polygon for the current cameraId+zoneId. Never stores a null/failed read. */
export function nextShowcaseLastKnownGoodPolygon(args: {
  cameraId?: string | null;
  zoneId?: string | null;
  livePolygon?: number[][] | null;
  previous: ShowcaseLastKnownGoodPolygon | null;
}): ShowcaseLastKnownGoodPolygon | null {
  const cameraId = text(args.cameraId);
  const zoneId = text(args.zoneId);
  const hasScope = Boolean(cameraId && zoneId);
  const sameScope = Boolean(
    args.previous
    && hasScope
    && args.previous.cameraId === cameraId
    && args.previous.zoneId === zoneId,
  );
  if (isRealZonePolygon(args.livePolygon) && hasScope) {
    return { cameraId, zoneId, polygon: copyRealZonePolygon(args.livePolygon) };
  }
  return sameScope ? args.previous : null;
}

/**
 * Live-camera overlay points only. Never returns the demo SHOWCASE_BOOTH_ZONE rectangle.
 * A: real polygon → that polygon. B: transient null → last known good. C: never read → [].
 */
export function resolveShowcaseBoothLiveOverlayPolygon(args: {
  livePolygon?: number[][] | null;
  lastKnownGood: ShowcaseLastKnownGoodPolygon | null;
}): number[][] {
  if (isRealZonePolygon(args.livePolygon)) return copyRealZonePolygon(args.livePolygon);
  if (args.lastKnownGood && isRealZonePolygon(args.lastKnownGood.polygon)) {
    return copyRealZonePolygon(args.lastKnownGood.polygon);
  }
  return [];
}

function zoneRowId(zone: Partial<Zone> | null | undefined): string {
  return text(zone?.zone_id || zone?.id);
}

function zoneRowCameraId(zone: Partial<Zone> | null | undefined): string {
  return text(zone?.camera_id);
}

/** Same camera+condition zone Spatial Operations overlays. Never the booth RAE zone. */
export function selectShowcaseForkliftZone(args: {
  zones: Array<Partial<Zone>> | null | undefined;
  cameraId?: string | null;
  zoneId?: string | null;
  excludeZoneIds?: Array<string | null | undefined>;
}): Partial<Zone> | null {
  const wantedZone = text(args.zoneId);
  const wantedCamera = text(args.cameraId);
  const excluded = new Set((args.excludeZoneIds || []).map((value) => text(value)).filter(Boolean));
  const rows = (Array.isArray(args.zones) ? args.zones : []).filter((zone) => {
    const id = zoneRowId(zone);
    if (!id || excluded.has(id)) return false;
    const camera = zoneRowCameraId(zone);
    return !wantedCamera || !camera || camera === wantedCamera;
  });
  if (wantedZone) {
    const matched = rows.find((zone) => zoneRowId(zone) === wantedZone) || null;
    if (matched && zoneOverlayPolygon(matched)) return matched;
  }
  return rows.find((zone) => {
    const polygon = zoneOverlayPolygon(zone);
    return Boolean(polygon && polygon.length >= 3);
  }) || null;
}

export function showcaseForkliftOverlayPolygon(zone: Partial<Zone> | null | undefined): number[][] | null {
  const polygon = zoneOverlayPolygon(zone);
  return polygon && polygon.length >= 3 ? polygon : null;
}
